<template>
  <div class="home">
    <!-- Banner -->
    <section class="banner">
      <img class="banner-img" src="https://vcos.vds.pub/vds-pub-fonts%2Fsupportcenter%2Fsupport-banner.png"
        alt="banner" />
      <div class="banner-overlay"></div>

      <!-- 欢迎文字和搜索框 -->
      <div class="banner-text">
        <h1 class="banner-text-h1">
          在这里，找到你需要的答案
        </h1>
        <p>欢迎访问 VDS 支持中心</p>

        <!-- 搜索框 -->
        <div class="search-box">
          <i class="search-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </i>
          <input class="search-input" type="text" placeholder="遇到什么问题了？" v-model="searchQuery"
            @keydown.enter="goSearch" />
        </div>
      </div>
    </section>

    <!-- 常见问题板块 -->
    <section class="common-questions">
      <h2 class="common-questions-title">常见问题</h2>
      
      <!-- 骨架屏加载效果 -->
      <div v-if="loadingCommonQuestions" class="skeleton-list">
        <div class="skeleton-item" v-for="i in 5" :key="i"></div>
      </div>
      
      <ul v-else class="common-questions-list">
        <li v-for="(item, index) in commonQuestions" :key="index" @click="goTo(item.url)">
          <span class="question-icon">📄</span>
          <span class="question-text">{{ item.title }}</span>
          <span class="question-arrow">→</span>
        </li>
      </ul>
    </section>

    <!-- 自助文档卡片 -->
    <h1 class="section-title">遇到了基础或共性问题？找找自助文档</h1>
    <div class="cards">
      <div v-for="(app, index) in apps" :key="index" class="card" @mouseenter="onHover(index)"
        @mouseleave="onLeave(index)" @click="goTo(app.url)" ref="cardEls">
        <img class="icon" :src="app.icon" :alt="app.title" />
        <h2 class="card-title">{{ app.title }}</h2>
        <p class="card-description">{{ app.description }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { gsap } from 'gsap'
import axios from 'axios'
import { DOCS_API_BASE } from '../../utils/docsApi'

const router = useRouter()

// 搜索框双向绑定
const searchQuery = ref('')

// 用户按下回车 -> 跳转到搜索结果页: /search?q=xxx
function goSearch() {
  if (!searchQuery.value.trim()) return
  router.push({
    name: 'search',
    query: { q: searchQuery.value.trim() }
  })
}

// 常见问题 (动态获取)
const commonQuestions = ref([])
const loadingCommonQuestions = ref(true)

function goTo(path) {
  router.push(path)
}

// 文档卡片
const apps = ref([
  {
    icon: '/img/account-logo.png',
    title: 'VDS账户',
    description: '账户相关指南',
    url: '/read?path=VDS账户'
  },
  {
    icon: 'https://furtvbeta.vds.pub/images/entnetappicon.png',
    title: '兽频道',
    description: '相关功能文档和指南',
    url: '/read?path=Fursuit.TV 兽频道'
  },
  {
    icon: 'https://developer.vds.pub/brand.png',
    title: 'VDS Developer',
    description: '开发者平台使用教程',
    url: '/read?path=VDS Developer'
  },
  {
    icon: 'https://vcos.vds.pub/vds-pub-fonts%2Fsupportcenter%2Flivescope.png',
    title: 'LiveScope',
    description: '直播相关文档和指南',
    url: '/read?path=LiveScope'
  }
])

// 卡片 DOM
const cardEls = ref([])

// 卡片hover动画 - 简单的0.3s动画
function onHover(index) {
  if (cardEls.value[index]) {
    gsap.to(cardEls.value[index], {
      scale: 1.05,
      y: -5,
      duration: 0.1,
    })
  }
}

function onLeave(index) {
  if (cardEls.value[index]) {
    gsap.to(cardEls.value[index], {
      scale: 1,
      y: 0,
      duration: 0.1,
    })
  }
}

// 加载热门文档
async function loadPopularDocs() {
  try {
    loadingCommonQuestions.value = true
    const res = await axios.get(`${DOCS_API_BASE}/popular`)
    if (res.data && Array.isArray(res.data)) {
      commonQuestions.value = res.data.map(doc => ({
        title: doc.title,
        url: `/read?path=${encodeURIComponent(doc.dirPath)}&name=${encodeURIComponent(doc.fileName)}`
      }))
    }
  } catch {
    // Documentation is optional and has its own loading state.
  } finally {
    loadingCommonQuestions.value = false
  }
}

onMounted(() => {
  document.title = "支持中心 - VDS·欢乐，在此发生。"
  loadPopularDocs() // 加载热门文档
})
</script>

<style scoped>
.home {
  color: white;
  text-align: center;
  padding: 20px;
  min-height: 100vh;
}

/* Banner */
.banner {
  position: relative;
  width: 100%;
  height: 400px;
  margin: 0 auto 40px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.banner-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 0;
}

.banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.5) 100%);
  border-radius: 12px;
  z-index: 0;
}

.banner-text {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 90%;
}

.banner-text-h1 {
  font-size: 50px;
  font-family: 'MiSans-Bold', Arial, sans-serif;
  margin: 0;
  padding: 0;
  line-height: 1.2;
  white-space: nowrap;
}

.banner-text p {
  font-size: 18px;
  margin: 10px 0 0;
  padding: 0;
  opacity: 0.9;
  white-space: nowrap;
}

/* 搜索框 */
.search-box {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 600px;
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
}

.search-icon {
  position: absolute;
  left: 20px;
  color: rgba(255, 255, 255, 0.7);
  z-index: 2;
  display: flex;
}

.search-input {
  width: 100%;
  padding: 16px 20px 16px 50px;
  border-radius: 25px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  color: white;
  font-size: 16px;
  outline: none;
  transition: all 0.3s ease;
  text-align: left;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.search-input:focus {
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(255, 255, 255, 0.1);
}

/* 用户信息卡片 */
.user-info-card {
  max-width: 700px;
  margin: 0 auto 50px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.user-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: white;
  font-family: 'MiSans-Bold', Arial, sans-serif;
}

.recent-tickets-title {
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 15px;
  font-family: 'MiSans-Bold', Arial, sans-serif;
}

.tickets-loading {
  text-align: center;
  padding: 20px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

.tickets-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
}

.ticket-item {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 15px;
}

.ticket-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.ticket-icon-wrapper {
  font-size: 24px;
  opacity: 0.8;
}

.ticket-content {
  flex: 1;
  text-align: left;
}

.ticket-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.ticket-short-id {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Courier New', monospace;
  background: rgba(255,255,255,0.1);
  padding: 1px 4px;
  border-radius: 4px;
  white-space: nowrap;
}

.ticket-title {
  font-size: 15px;
  color: white;
  font-weight: 500;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  border: 1px solid rgba(255, 255, 255, 0.16);
}

.status-open {
  color: #9fd0ff;
  background: rgba(0, 122, 255, 0.16);
}

.status-pending {
  color: #ffd889;
  background: rgba(250, 173, 20, 0.16);
}

.status-closed {
  color: #b6f2c2;
  background: rgba(82, 196, 26, 0.16);
}

.ticket-meta {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  align-items: center;
  font-size: 12px;
}

.ticket-category {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 8px;
  border-radius: 6px;
}

.ticket-date {
  color: rgba(255, 255, 255, 0.5);
}

.ticket-followups {
  color: rgba(255, 255, 255, 0.5);
}

.ticket-continue {
  background: rgba(0, 106, 255, 0.16);
  color: #c9e4ff;
  border: 1px solid rgba(0, 162, 255, 0.28);
  border-radius: 10px;
  padding: 8px 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.ticket-continue:hover {
  background: rgba(0, 106, 255, 0.28);
  color: white;
}

.ticket-arrow {
  opacity: 0.5;
  transition: transform 0.3s;
}

.ticket-item:hover .ticket-arrow {
  transform: translateX(3px);
  opacity: 1;
}

.tickets-empty {
  text-align: center;
  padding: 30px 20px;
}

.empty-icon {
  font-size: 36px;
  margin-bottom: 10px;
  opacity: 0.3;
}

.empty-text {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
}

/* 常见问题板块 */
.common-questions {
  margin: 0 auto 50px;
  max-width: 700px;
  text-align: left;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.common-questions-title {
  font-size: 28px;
  margin-bottom: 20px;
  font-family: 'MiSans-Bold', Arial, sans-serif;
  text-align: center;
}

.common-questions-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
}

.common-questions-list li {
  display: flex;
  align-items: center;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
}


.common-questions-list li:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.2);
}

.question-icon {
  margin-right: 12px;
  font-size: 20px;
}

.question-text {
  flex: 1;
  font-size: 15px;
  font-weight: 500;
}

.question-arrow {
  opacity: 0.3;
  font-size: 18px;
}

/* 骨架屏样式 */
.skeleton-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
}

.skeleton-item {
  height: 56px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

/* 自助文档卡片 */
.section-title {
  margin: 60px 0 30px;
  font-size: 32px;
  font-family: 'MiSans-Bold', Arial, sans-serif;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 800px;
  margin: 0 auto 60px;
}

.card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 30px;
  cursor: pointer;
  transform-origin: center center;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.card:hover {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
}

.icon {
  width: 80px;
  height: 80px;
  margin-bottom: 15px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.card-title {
  font-size: 20px;
  margin: 0 0 10px;
  font-family: 'MiSans-Bold', Arial, sans-serif;
}

.card-description {
  font-size: 14px;
  opacity: 0.8;
  margin: 0;
  line-height: 1.5;
}

.explanation {
  margin-top: 15px;
  font-size: 16px;
  opacity: 0.8;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 响应式 */
@media (max-width: 768px) {
  .home {
    padding: 15px;
  }

  .banner {
    height: 350px;
  }

  .banner-text-h1 {
    font-size: 32px;
  }

  .banner-text p {
    font-size: 16px;
  }

  .search-input {
    max-width: 85%;
    font-size: 15px;
  }

  .user-info-card {
    padding: 20px;
  }

  .recent-tickets-title {
    white-space: normal;
    word-wrap: break-word;
  }

  .ticket-title {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    line-height: 1.4;
  }

  .ticket-item {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .ticket-content {
    min-width: 0;
  }

  .ticket-header,
  .ticket-meta {
    flex-wrap: wrap;
  }

  .ticket-continue {
    margin-left: 39px;
  }

  .tickets-list {
    grid-template-columns: 1fr;
  }

  .common-questions {
    padding: 20px;
  }

  .section-title {
    font-size: 24px;
  }

  .card {
    padding: 25px;
  }

  .cards {
    grid-template-columns: 1fr;
    max-width: 500px;
  }
}

@media (max-width: 480px) {
  .banner {
    height: 300px;
  }

  .banner-text-h1 {
    font-size: 24px;
  }

  .banner-text p {
    font-size: 14px;
  }

  .search-input {
    max-width: 90%;
    font-size: 14px;
    padding: 12px 16px;
  }

  .user-info-card {
    padding: 20px;
  }

  .section-title {
    font-size: 20px;
  }

  .card {
    padding: 20px;
  }
}
</style>
